import { Injector, computed, runInInjectionContext, signal } from '@angular/core';
import { IData } from '@core/models/IApi';
import { DimCategory, Dimension } from '@features/social-analysis/models/IDimensions';
import { ColumnForTable, TableInit } from '@features/social-analysis/models/ITable';
import { SocListnInsTableHttpBuilder } from '@features/social-analysis/social-listening/services/ins-table.http';
import { PageFiltersState } from './page-filters.store';
import { TableState } from './table.store';

type InsTableInit = {
    dimOpts: DimCategory[];
    dimensions: Dimension[];
} & TableInit;

export abstract class InsightsTableState<T = IData, K = T> extends TableState<T, K> {
    dimOpts;
    dimensions = signal<Dimension[]>([]);
    selectableDims = computed(() => this.dimensions().filter(dim => !dim.fixed));
    override limit = signal(50);

    override colsForTable = computed(() => [
        ...this.#getDimsAsCols(),
        ...this.computeDisplayCols(),
    ]);

    constructor(
        { component, columns, selectedCols, sorting, dimOpts, dimensions }: InsTableInit,
        injector: Injector
    ) {
        super({ component, columns, selectedCols, sorting }, injector);
        this.dimOpts = dimOpts;
        this.dimensions.set(dimensions);
    }

    setDimensions(dims: Dimension[]) {
        const fixedDim = this.dimensions().filter(dim => dim.fixed);
        this.dimensions.set([...fixedDim, ...dims]);

        const fixedSort = fixedDim.map(dim => ({ prop: dim.value, dir: 'asc' }));
        const newSorts = dims.map(dim => ({ prop: dim.value, dir: 'asc' }));
        this.sorting.set([...fixedSort, ...newSorts]);
    }

    #getDimsAsCols() {
        const current = this.dimensions().map(
            (dim: Dimension): ColumnForTable => this.#formatDimToCol(dim)
        );
        return current;
    }

    #formatDimToCol(dim: Dimension) {
        return {
            prop: dim?.value,
            name: dim?.display,
            isSubHeader: true,
        };
    }
}

export class ListeningInsTable extends InsightsTableState {
    #http!: SocListnInsTableHttpBuilder;

    constructor(init: InsTableInit, injector: Injector) {
        super(init, injector);
        runInInjectionContext(injector, () => (this.#http = new SocListnInsTableHttpBuilder()));
    }

    reqData(pageFilters: PageFiltersState, needsTotal?: boolean) {
        this.#http.reqData(this, pageFilters, needsTotal);
    }

    reqTotal(pageFilters: PageFiltersState): void {}
}
